import { supabase } from "@/integrations/supabase/client";

/**
 * Lättviktig händelseloggning för adminpanelens statistiksida.
 * Allt är "fire and forget" – loggningen får aldrig blockera eller krascha UI.
 */

const SESSION_KEY = "em_session_id";

export const getSessionId = (): string | null => {
  if (typeof window === "undefined") return null;
  try {
    let id = window.sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = crypto.randomUUID();
      window.sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
};

const getDevice = (): string => {
  if (typeof window === "undefined") return "okänd";
  const w = window.innerWidth;
  if (w < 640) return "mobil";
  if (w < 1024) return "surfplatta";
  return "dator";
};

const getReferrer = (): string | null => {
  if (typeof document === "undefined") return null;
  const ref = document.referrer;
  if (!ref) return null;
  try {
    const url = new URL(ref);
    if (url.hostname === window.location.hostname) return null;
    return url.hostname.replace(/^www\./, "");
  } catch {
    return null;
  }
};

const swallow = (p: PromiseLike<unknown>) => {
  void Promise.resolve(p).then(
    () => undefined,
    () => undefined,
  );
};

export const trackPageView = (path: string, title?: string) => {
  if (typeof window === "undefined") return;
  if (path.startsWith("/admin")) return;
  swallow(
    supabase.from("page_views").insert({
      path,
      title: title ?? document.title ?? null,
      referrer: getReferrer(),
      device: getDevice(),
      session_id: getSessionId(),
    }),
  );
};

export type FunnelStage = "impression" | "start" | "complete";

export interface FunnelEventInput {
  goal: string;
  stage: FunnelStage;
  formKey?: string;
  step?: number;
  metadata?: Record<string, unknown>;
}

export const trackFunnel = ({ goal, stage, formKey, step, metadata }: FunnelEventInput) => {
  if (typeof window === "undefined") return;
  swallow(
    supabase.from("funnel_events").insert({
      goal,
      stage,
      form_key: formKey ?? null,
      step: typeof step === "number" ? step : null,
      path: window.location.pathname,
      session_id: getSessionId(),
      metadata: (metadata ?? {}) as never,
    }),
  );
};

/** Loggar en impression en gång per montering. */
export const GOAL_LABELS: Record<string, string> = {
  elavtal: "Elavtal – sökflöde",
  foretagsoffert: "Företagsoffert",
  offert: "Offertförfrågan (alla)",
  offert_solceller: "Offert – solceller",
  offert_hembatterier: "Offert – hembatterier",
  offert_varmepumpar: "Offert – värmepumpar",
  offert_laddboxar: "Offert – laddboxar",
  offert_laddstolpar: "Offert – laddstolpar",
  "offert_kommersiella-solceller": "Offert – kommersiella solceller",
  "offert_kommersiella-batterier": "Offert – kommersiella batterier",
  fullmakt: "Fullmakt",
  support: "Kundservice / support",
  partner: "Bli partner",
  checkout: "Teckna elavtal",
  inloggning: "Inloggningar",
  konto: "Skapade konton",
};

/** Grupperar mål i kategorier för statistikens undersidor. */
export const GOAL_GROUPS: { key: string; label: string; goals: string[] }[] = [
  {
    key: "offert",
    label: "Offertförfrågningar",
    goals: [
      "offert",
      "offert_solceller",
      "offert_hembatterier",
      "offert_varmepumpar",
      "offert_laddboxar",
      "offert_laddstolpar",
      "offert_kommersiella-solceller",
      "offert_kommersiella-batterier",
      "foretagsoffert",
    ],
  },
  { key: "elavtal", label: "Elavtal", goals: ["elavtal", "checkout", "fullmakt"] },
  { key: "konto", label: "Konton & inloggning", goals: ["inloggning", "konto"] },
  { key: "ovrigt", label: "Övrigt", goals: ["support", "partner"] },
];

export const goalGroupFor = (goal: string) =>
  GOAL_GROUPS.find((g) => g.goals.includes(goal))?.key ?? "ovrigt";

export const goalLabel = (goal: string) => GOAL_LABELS[goal] ?? goal;

/** Extra metadatanycklar som är intressanta att bryta ner ett mål på. */
export const GOAL_META_KEYS: Record<string, { key: string; label: string }[]> = {
  offert: [
    { key: "services", label: "Tjänster" },
    { key: "customerType", label: "Kundtyp" },
    { key: "product", label: "Produkt" },
  ],
  elavtal: [
    { key: "flow", label: "Flöde" },
    { key: "customerType", label: "Kundtyp" },
    { key: "companyKind", label: "Bolagstyp" },
    { key: "source", label: "Källa" },
  ],
  checkout: [
    { key: "elbolag", label: "Elbolag" },
    { key: "avtalstyp", label: "Avtalstyp" },
    { key: "elomrade", label: "Elområde" },
  ],
  inloggning: [
    { key: "method", label: "Metod" },
    { key: "location", label: "Plats" },
  ],
  konto: [
    { key: "method", label: "Metod" },
    { key: "location", label: "Plats" },
  ],
};

